import { useMemo, useRef } from 'react';

import { selectedAgentTransition } from './selected-agent-transition-reducer';
import {
	type SelectedAgentTransitionState,
	selectedAgentTransitionInternals,
} from './selected-agent-transition-state';

/**
 * Stable handle over the selected-agent transition state machine.
 *
 * Consumers dispatch named events and read named predicates; the underlying
 * ref and reducer are owned here so there is exactly one state instance per
 * prompt key. Callbacks read the ref internally, so their identity is stable
 * across renders and never defeats downstream memo boundaries.
 */
export type SelectedAgentTransitionHandle = {
	shouldHydrateDraftMentions: (
		draftAgentIds: readonly string[],
		selectedAgentIds: readonly string[],
	) => boolean;
	shouldRemoveDestroyedMention: (agentId: string) => boolean;
	isSubmitClearing: () => boolean;
	markDraftMentionsSeen: (draftAgentIds: readonly string[]) => void;
	onReasoningMenuOpenChange: (isOpen: boolean) => void;
	onSubmitClearChange: (isClearing: boolean) => void;
};

export const useSelectedAgentTransition = (
	promptDraftAndModeKey: string,
): SelectedAgentTransitionHandle => {
	const stateRef = useRef<SelectedAgentTransitionState>(
		selectedAgentTransitionInternals.createState(promptDraftAndModeKey),
	);
	// Sync the prompt key during render so the freshest key is used before any
	// consumer reads a predicate this pass. The reducer treats a same-key event
	// as a no-op, keeping this idempotent under repeated/StrictMode renders.
	stateRef.current = selectedAgentTransitionInternals.reduce(stateRef.current, {
		type: 'conversationDraftKeyChanged',
		promptKey: promptDraftAndModeKey,
	});

	return useMemo<SelectedAgentTransitionHandle>(
		() => ({
			shouldHydrateDraftMentions: (draftAgentIds, selectedAgentIds) =>
				selectedAgentTransition.shouldHydrateDraftMentionIds({
					state: stateRef.current,
					draftAgentIds,
					selectedAgentIds,
				}),
			shouldRemoveDestroyedMention: (agentId) =>
				selectedAgentTransition.shouldRemoveMentionDestroyedAgent({
					state: stateRef.current,
					agentId,
				}),
			isSubmitClearing: () => stateRef.current.isSubmitClearing,
			markDraftMentionsSeen: (draftAgentIds) => {
				stateRef.current = selectedAgentTransitionInternals.reduce(stateRef.current, {
					type: 'draftMentionIdsSeen',
					agentIds: draftAgentIds,
				});
			},
			onReasoningMenuOpenChange: (isOpen) => {
				stateRef.current = selectedAgentTransitionInternals.reduce(stateRef.current, {
					type: 'reasoningModeMenuOpenChanged',
					isOpen,
				});
			},
			onSubmitClearChange: (isClearing) => {
				stateRef.current = selectedAgentTransitionInternals.reduce(stateRef.current, {
					type: 'submitClearInProgressChanged',
					isClearing,
				});
			},
		}),
		[],
	);
};
